import { useFormContext } from "react-hook-form";
import Subtitle from "../subtitle/subtitle";
import {
  AddClientButton,
  AddClientButtonContainer,
  BaseForm,
  InitialDetailsStyled,
} from "./initial-details.styles";
import { Add } from "@mui/icons-material";
import AddNewClientModal from "./add-new-client-modal/add-new-client-modal";
import { useContext, useEffect, useState } from "react";
import { getClients } from "../../../../actions";
import useDebouncedCallback from "../../../../hooks/useDebouncedCallback";
import AutocompleteFormField from "../../../../components/form-fields/autocomplete-field/autocomplete-form-field";
import { Clients } from "./types";
import { StepperDataContext } from "../../context/stepper-context";
import { CircularProgress } from "@mui/material";
import SelectFormField from "../../../../components/form-fields/select-field/select-form-field";
import AsyncPaginateFormField from "../../../../components/form-fields/asyncpaginate-field/async-paginate-field";
import { loadAddressOptions } from "../../../../utils/common-utils";
import { GlobalContext } from "../../../../context/GlobalProvider";

const Loader = () => (
  <div className="loader">
    <CircularProgress size="20px" />
  </div>
);

const InitialDetails: React.FC = () => {
  const [clients, setClients] = useState<Clients>([]);
  const [loading, setLoading] = useState<Boolean>(false);
  const {
    selectedClient,
    setSelectedClient,
    setSelectedPatients,
    setSelectedTimeSlot,
    setSelectedProvider,
    selectedProvider,
  } = useContext(StepperDataContext);
  const { providersAppointmentData } = useContext(GlobalContext);
  const { control, watch, setValue, trigger } = useFormContext();

  const [modalState, setModalState] = useState<{
    addNewClient: boolean;
  }>({
    addNewClient: false,
  });

  const handleAddClientButton = () =>
    setModalState((state) => ({ ...state, addNewClient: true }));

  const handleClientSearch = useDebouncedCallback((search) => {
    setLoading(true);
    getClients(search || "")
      .then((response) => {
        const { data } = response.data;
        setClients(data);
        if (selectedClient) {
          const { address, city, state, country, zipcode } = selectedClient;
          const fullAddress =
            `${address ? address + "," : ""} ${city ? city + "," : ""} ${state ? state + "," : ""} ${country ? country + "," : ""} ${zipcode}`.replace(
              /[\s+,+]$/,
              ""
            );
          // setValue("address", fullAddress);

          setValue("address", {
            label: fullAddress,
            value: fullAddress,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        setClients([]);
        setLoading(false);
      });
  });

  const resetModalState = () =>
    setModalState((state) => ({ ...state, addNewClient: false }));

  const handleClientChange = (
    event: any,
    value: { id: string; label: string }
  ) => {
    try {
      const { id } = value;
      const client = clients.find((client) => client.integration_id === id);
      let fullAddress = "";
      if (client) {
        setSelectedClient(client);
        const { address, city, state, country, zipcode } = client;

        fullAddress =
          `${address ? address + "," : ""} ${city ? city + "," : ""} ${state ? state + "," : ""} ${country ? country + "," : ""} ${zipcode}`.replace(
            /[\s+,+]$/,
            ""
          );
      }
      // setValue("address", fullAddress);
      setValue("address", {
        label: fullAddress,
        value: fullAddress,
      });
      trigger();
    } catch (e) {
      // handle if value is null
    } finally {
      setSelectedPatients([]);
      setSelectedTimeSlot(null);
    }
  };

  useEffect(() => {
    const { search }: { search?: string } = { ...watch() };
    if (search?.trim()) handleClientSearch(search);
    else setClients([]);
  }, [watch().search]);

  useEffect(() => {
    if (selectedClient) {
      const { first_name, last_name } = selectedClient;
      setValue("search", `${first_name} ${last_name}`.trim());
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedClient) {
      setValue(
        "appointment_for",
        selectedProvider?.actor_id || providersAppointmentData?.[0]?.actor_id
      );
    }
  }, [
    providersAppointmentData,
    selectedClient,
    selectedProvider?.actor_id,
    setValue,
  ]);

  return (
    <InitialDetailsStyled>
      <Subtitle title="Enter initial details below" />
      <BaseForm>
        <SelectFormField
          name="appointment_for"
          label="Schedule For"
          control={control}
          options={providersAppointmentData?.map((x: any) => {
            return {
              ...x,
              label: x?.display_name,
              value: x?.actor_id,
            };
          })}
          customOnChange={(event) => {
            setSelectedProvider(
              providersAppointmentData?.find(
                (x: any) => x?.actor_id === event?.target?.value
              )
            );
          }}
          defaultValue={
            selectedProvider?.actor_id ||
            providersAppointmentData?.[0]?.actor_id
          }
        />
        <div className="autocomeplete-input">
          <AutocompleteFormField
            name="search"
            control={control}
            label="Search client"
            placeholder="Search client name/phone number here"
            onSelect={handleClientChange}
            options={clients.map((client, index) => {
              return {
                id: client.integration_id,
                label: `${`${index + 1}. ${client.first_name} ${client.last_name}`.trim()}`,
              };
            })}
          />
          {loading && <Loader />}
        </div>
        {/* <TextInputFormField
          name="address"
          control={control}
          label="Address"
          placeholder="Enter here"
        /> */}
        <AsyncPaginateFormField
          id="address"
          name="address"
          label="Address"
          control={control}
          loadOptions={loadAddressOptions}
          placeholder="Type your address..."
        />
      </BaseForm>
      <AddClientButtonContainer>
        <AddClientButton
          onClick={handleAddClientButton}
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
        >
          <Add />
          Add new client
        </AddClientButton>
      </AddClientButtonContainer>
      <AddNewClientModal
        setValue={setValue}
        setClients={setClients}
        handleClientChange={handleClientChange}
        open={modalState.addNewClient}
        onClose={resetModalState}
      />
    </InitialDetailsStyled>
  );
};

export default InitialDetails;
